// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { Moves } from '@gql/base-types';
import { Box, Typography } from '@mui/material';
import { BiErrorCircle } from 'react-icons/bi';
import { css } from '@emotion/css';
import { Theme, useTheme } from '@mui/material';
import { getConfirmationSummary } from '@features/moves/utils/getConfirmationStatus';
import {
  requiresPickupConfirmation,
  requiresDeliveryConfirmation,
} from '@features/moves/utils/checkConfirmationRequirement';

// TYPES ---------------------------------------------------------------- //

interface ConfirmationBannerProps {
  move?: Moves;
}

// COMPONENT ---------------------------------------------------------------- //

const ConfirmationBanner = ({ move }: ConfirmationBannerProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  if (!move) return null;

  // Only show banner for unconfirmed moves
  if (move.status !== 'unconfirmed') return null;

  // Check what requires confirmation
  const pickupRequired = requiresPickupConfirmation(move);
  const deliveryRequired = requiresDeliveryConfirmation(move);

  // Only show banner if pickup OR delivery requires confirmation
  if (!pickupRequired && !deliveryRequired) return null;

  const confirmationSummary = getConfirmationSummary(move);
  if (!confirmationSummary) return null;

  // Check for raw database status "ready" (not the mapped "confirmed")
  const pickupConfirmed = confirmationSummary.pickup.status?.toLowerCase() === 'ready';
  const deliveryConfirmed = confirmationSummary.delivery.status?.toLowerCase() === 'ready';

  // Don't show banner if all required locations are confirmed (shouldn't happen with unconfirmed status, but safety check)
  const allRequiredConfirmed = (!pickupRequired || pickupConfirmed) && (!deliveryRequired || deliveryConfirmed);
  if (allRequiredConfirmed) return null;

  // Determine main message based on what's required
  let mainMessage = '';
  if (pickupRequired && deliveryRequired) {
    mainMessage = 'This move cannot be dispatched until both pickup and delivery locations confirm readiness.';
  } else if (pickupRequired) {
    mainMessage = 'This move cannot be dispatched until the pickup location confirms readiness.';
  } else if (deliveryRequired) {
    mainMessage = 'This move cannot be dispatched until the delivery location confirms readiness.';
  }

  // Determine status description based on what's required and what's confirmed
  let descriptionText = '';
  if (pickupRequired && deliveryRequired) {
    // Both required
    if (!pickupConfirmed && !deliveryConfirmed) {
      descriptionText = 'Neither location has confirmed yet.';
    } else if (pickupConfirmed && !deliveryConfirmed) {
      descriptionText = 'Pickup confirmed — awaiting delivery confirmation.';
    } else if (!pickupConfirmed && deliveryConfirmed) {
      descriptionText = 'Delivery confirmed — awaiting pickup confirmation.';
    }
  } else if (pickupRequired) {
    // Only pickup required
    if (!pickupConfirmed) {
      descriptionText = 'Pickup location has not confirmed yet.';
    }
  } else if (deliveryRequired) {
    // Only delivery required
    if (!deliveryConfirmed) {
      descriptionText = 'Delivery location has not confirmed yet.';
    }
  }

  return (
    <Box className={cls.banner}>
      <Box className={cls.bannerContent}>
        <Box className={cls.bannerIcon}>
          <BiErrorCircle size={24} />
        </Box>
        <Box className={cls.bannerText}>
          <Typography variant='h6' className={cls.bannerTitle}>
            Confirmation Required
          </Typography>
          <Typography variant='body2' className={cls.bannerDescription}>
            {mainMessage} {descriptionText}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme: Theme) => ({
  banner: css`
    background-color: #ffffff !important;
    border: 2px solid ${theme.palette.warning.main};
    border-left: 4px solid ${theme.palette.warning.main};
    border-radius: 8px;
    padding: 20px 24px;
    margin-top: 0;
    margin-bottom: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  `,
  bannerContent: css`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 16px;
    ${theme.breakpoints.down('md')} {
      flex-direction: column;
      gap: 12px;
    }
  `,
  bannerIcon: css`
    padding: 10px;
    background-color: ${theme.palette.warning.main};
    color: ${theme.palette.common.white};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 44px;
    height: 44px;
  `,
  bannerText: css`
    flex: 1;
    min-width: 0;
  `,
  bannerTitle: css`
    font-weight: 600;
    color: ${theme.palette.text.primary};
    margin-bottom: 8px;
    line-height: 1.4;
    font-size: 1.125rem;
  `,
  bannerDescription: css`
    color: ${theme.palette.text.secondary};
    line-height: 1.6;
    font-size: 0.875rem;
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default ConfirmationBanner;
